'use client';

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import MinusIcon from '@/fo/icons/base/icons/minus_bold.svg';
import PlusIcon from '@/fo/icons/base/icons/plus_bold.svg';
import { UiKitButtonBase, ButtonColor } from '@/fo/ui/kit/button/base';
import { UiKitCollapseBase } from '@/fo/ui/kit/collapse/base';
import { UiModScrollDraggable } from '@/fo/ui/mod/scroll/draggable';
import { utilScrollToElement } from '@/fo/util/scroll-to-element';

import { styles } from './UiKitPanelBase.styles';

type Variants = VariantProps<typeof styles>;

export interface UiKitPanelBaseProps {
  activeIndex?: number;
  className?: string;
  tabsClassName?: string;
  tabClassName?: string;
  tabActiveClassName?: string;
  contentClassName?: string;
  tabsWrapClassName?: string;
  title?: string | null;
  tabActiveColor?: ButtonColor;
  view?: Variants['view'];
  items: {
    header: React.ReactNode;
    content: string | React.ReactNode;
    disabled?: boolean;
  }[];
  onTabChange?: (index: number) => void;
}

export function UiKitPanelBase({
  activeIndex: activeIndexProp = 0,
  view = 'auto',
  items,
  className,
  tabsClassName,
  tabClassName,
  tabActiveClassName = '',
  contentClassName,
  tabsWrapClassName,
  title,
  onTabChange
}: UiKitPanelBaseProps) {
  const [activeIndex, setActiveIndex] = useState(activeIndexProp);
  const headerRefs = useRef<HTMLDivElement[]>([]);
  const contentRefs = useRef<HTMLDivElement[]>([]);
  const slots = styles({ view });
  const scrollTargetRef = useRef<HTMLDivElement | null>(null);

  const setHeaderRef = useCallback(
    (index: number) => (element: HTMLDivElement | null) => {
      if (element) {
        headerRefs.current[index] = element;
      }
    },
    []
  );

  const setContentRef = useCallback(
    (index: number) => (element: HTMLDivElement | null) => {
      if (element) {
        contentRefs.current[index] = element;
      }
    },
    []
  );

  useEffect(() => {
    setActiveIndex(activeIndexProp);
  }, [activeIndexProp]);

  const handleClick = (index: number) => () => {
    setActiveIndex(index);
    onTabChange?.(index);

    if (view === 'auto') {
      scrollTargetRef.current = contentRefs.current[index];
    }
  };

  return (
    <div className={slots.base({ className })}>
      {title && <p className={slots.title()}>{title}</p>}
      <UiModScrollDraggable
        className={slots.tabs({ className: tabsClassName })}
      >
        <div className={slots.tabsWrap({ className: tabsWrapClassName })}>
          {items.map((item, index) => (
            <Fragment key={index}>
              {view !== 'additionalTab' && (
                <UiKitButtonBase
                  rounded="full"
                  onClick={handleClick(index)}
                  disabled={item.disabled}
                  className={
                    item.disabled
                      ? `${tabClassName} opacity-60`
                      : `${tabClassName} ${activeIndex === index ? tabActiveClassName : ''}`
                  }
                >
                  {item.header}
                </UiKitButtonBase>
              )}
              {view === 'additionalTab' && (
                <button
                  type="button"
                  className={slots.additionalTab({
                    active: activeIndex === index
                  })}
                  onClick={handleClick(index)}
                >
                  {item.header}
                </button>
              )}
            </Fragment>
          ))}
        </div>
      </UiModScrollDraggable>

      {items.map((item, index) => (
        <Fragment key={index}>
          <div
            ref={setHeaderRef(index)}
            className={slots.header({
              active: activeIndex === index
            })}
            onClick={() => {
              setActiveIndex(activeIndex === index ? -1 : index);

              if (view === 'auto') {
                scrollTargetRef.current = headerRefs.current[index];
              }
            }}
          >
            {item.header}
            <span className={slots.headerIcon()}>
              {activeIndex === index && <MinusIcon />}
              {activeIndex !== index && <PlusIcon />}
            </span>
          </div>
          <UiKitCollapseBase
            className={slots.wrapper({ className: contentClassName })}
            collapse={activeIndex !== index}
            onTransitionEnd={() => {
              if (scrollTargetRef.current) {
                utilScrollToElement(scrollTargetRef.current);
                scrollTargetRef.current = null;
              }
            }}
          >
            <div
              ref={setContentRef(index)}
              className={slots.content({
                active: activeIndex === index
              })}
              dangerouslySetInnerHTML={
                typeof item.content === 'string'
                  ? { __html: item.content }
                  : undefined
              }
            >
              {typeof item.content === 'string' ? null : item.content}
            </div>
          </UiKitCollapseBase>
        </Fragment>
      ))}
    </div>
  );
}
